import React, {useState, useEffect} from "react";
import {sweetAlertQuestion, sweetAlerts} from "../../utils/sweet-alerts";
import {getAllContactUs, updateContactUsStatus, deleteContactUs, getUserList} from "../../services/adminServices";
import CustomLoader from "../../CustomComponents/CustomLoader";
import moment from "moment";
import {Autocomplete, TextField, CircularProgress} from "@mui/material";
import CustomUserName from "../../CustomComponents/CustomUserName";
import CustomPagination from "../../CustomComponents/CustomPagination";

const ContactUs = () => {
	const [firstTimeLoad, setFirstTimeLoad] = useState(true);
	const [contacts, setContacts] = useState([]);
	const [filteredContacts, setFilteredContacts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedContact, setSelectedContact] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [filterStatus, setFilterStatus] = useState("all");
	const [sortBy, setSortBy] = useState("date-desc");
	const [page, setPage] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [totalPages, setTotalPages] = useState(1);
	const [totalLength, setTotalLength] = useState(0);
	const [loadingButtons, setLoadingButtons] = useState({});

	// User search functionality
	const [open, setOpen] = useState(false);
	const [options, setOptions] = useState([]);
	const [loadingUser, setLoadingUser] = useState(true);
	const [debounceTimer, setDebounceTimer] = useState(null);
	const [selectedUserId, setSelectedUserId] = useState(null);
	const [inputValue, setInputValue] = useState("");

	const statusOptions = [
		{value: "all", label: "All Status"},
		{value: "pending", label: "Pending"},
		{value: "reviewed", label: "Reviewed"},
		{value: "resolved", label: "Resolved"},
		{value: "closed", label: "Closed"},
	];

	const getStatusBadge = (status) => {
		const statusConfig = {
			pending: {class: "badge-warning", text: "Pending"},
			reviewed: {class: "badge-info", text: "Reviewed"},
			resolved: {class: "badge-success", text: "Resolved"},
			closed: {class: "badge-danger", text: "Closed"},
		};

		const config = statusConfig[status] || {
			class: "badge-secondary",
			text: status,
		};
		return <span className={`badge ${config.class}`}>{config.text}</span>;
	};

	const handleSearch = async (query) => {
		if (!query || query.length < 3) {
			setOptions([]);
			return;
		}
		setLoadingUser(true);

		const timer = setTimeout(async () => {
			try {
				const res = await getUserList({search: query});
				setOptions(res?.data);
			} catch (error) {
				console.error("API error:", error);
			} finally {
				setLoadingUser(false);
			}
		}, 300);

		setDebounceTimer(timer);
	};

	const fetchContacts = async () => {
		if (firstTimeLoad) {
			setLoading(false);
		}
		try {
			const params = {
				page: page,
				pageSize: pageSize,
				status: filterStatus,
				sortBy: sortBy,
				search: searchTerm,
				userId: selectedUserId,
			};

			const response = await getAllContactUs(params);

			if (response && response.status === true) {
				const transformedContacts =
					response?.data?.list?.map((contact) => ({
						...contact,
						userName: contact.user ? `${contact.user.first_name} ${contact.user.last_name}` : "N/A",
						submittedAt: contact.createdAt,
						fullName: `${contact.first_name} ${contact.last_name}`,
					})) || [];

				setTotalCount(response?.data?.totalCount);
				setLoading(true);
				setContacts(transformedContacts);
				setTotalLength(response?.data?.totalCount || 0);
				const totalPages = Math.ceil(response?.data?.totalCount / pageSize);
				setTotalPages(totalPages);
			} else {
				setLoading(true);
				setContacts([]);
			}
		} catch (error) {
			console.error("Error fetching contacts:", error);
			setContacts([]);
		}
	};

	const handleViewDetails = async (contact) => {
		try {
			setSelectedContact(contact);
			setShowModal(true);
		} catch (error) {
			console.error("Error fetching contact details:", error);
			sweetAlerts("error", "Failed to fetch contact details");
		}
	};

	const handleStatusUpdate = async (contactId, newStatus) => {
		const confirmed = await sweetAlertQuestion("Update Status", `Are you sure you want to change the status to ${newStatus}?`);

		if (!confirmed) return;

		try {
			const response = await updateContactUsStatus(contactId, {status: newStatus});
			if (response && response.status === true) {
				sweetAlerts("success", "Status updated successfully");
				fetchContacts();
			} else {
				sweetAlerts("error", "Failed to update status");
			}
		} catch (error) {
			console.error("Error updating status:", error);
			sweetAlerts("error", "Failed to update status");
		}
	};

	const paginate = (pageNumber) => setPage(pageNumber);

	const truncateText = (text, maxLength = 50) => {
		if (!text) return "N/A";
		return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
	};

	const formatDate = (date) => {
		return moment(date).format("MMM DD, YYYY");
	};

	useEffect(() => {
		fetchContacts();
	}, [pageSize, page]);

	useEffect(() => {
		setContacts(contacts);
		setFilteredContacts(contacts);
		setLoadingButtons({});
	}, [contacts]);

	useEffect(() => {
		if (firstTimeLoad) {
			setFirstTimeLoad(false);
		} else {
			setPage(1);
			fetchContacts();
		}
	}, [filterStatus, sortBy, selectedUserId, searchTerm]);

	const handleDeleteContact = async (contactId) => {
		sweetAlertQuestion("Are you sure you want to delete this? This action cannot be undone.").then(async (result) => {
			if (result === "Yes") {
				setLoadingButtons((prev) => ({...prev, [`delete-${contactId}`]: true}));
				try {
					const response = await deleteContactUs(contactId);
					if (response && response.status === true) {
						if (filteredContacts.length === 1) {
							setPage(page - 1);
						}
						fetchContacts();
						sweetAlerts("success", "Contact deleted successfully");
					} else {
						sweetAlerts("error", "Failed deleting contact. Please try again.");
					}
				} catch (error) {
					console.error("Error deleting contact:", error);
					sweetAlerts("error", "Failed deleting contact. Please try again.");
				} finally {
					setLoadingButtons((prev) => ({...prev, [`delete-${contactId}`]: false}));
				}
			}
		});
	};

	const columns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			width: "250px",
			ellipsis: {showTitle: false},
			// render: (_, item) => (
			// 	<Tooltip title={item?.full_address} arrow>
			// 		{truncateWords(item?.full_address, 30) || "-"}
			// 	</Tooltip>
			// ),
		},
		{
			title: "User Name",
			dataIndex: "user_name",
			key: "user_name",
			width: "250px",
			sorter: true,
			ellipsis: {showTitle: false},
			// render: (_, item) => <CustomUserName user={item?.user} />,
		},
		{
			title: "Rating",
			dataIndex: "rating",
			key: "rating",
			width: "180px",
			// sorter: true,
			// render: (_, item) => <CustomRatingStar star={item?.aggregate_rating} />,
		},
		{
			title: "Types",
			dataIndex: "manual_id",
			key: "manual_id",
			width: "180px",
			align: "center",
			sorter: true,
			// render: (_, item) => getAddressBadge(item?.manual_id),
		},
		{
			title: "Submitted",
			dataIndex: "submitted",
			key: "submitted",
			width: "180px",
			align: "center",
			sorter: true,
			// render: (_, item) => fDate(item?.createdAt),
		},
		{
			title: "Total Rating/Comment",
			dataIndex: "rating_comment",
			key: "rating_comment",
			width: "180px",
			// render: (_, item) => <>{item?.rating_count + "/" + item?.comment_count}</>,
		},
		{
			title: "Action",
			dataIndex: "Action",
			key: "Action",
			width: "100px",
			fixed: "right",
			align: "right",
			// render: (_, item) => (
			// 	<div className="table-actions mb-0 justify-content-end flex-row">
			// 		<button
			// 			className="btn-icon"
			// 			onClick={(e) => {
			// 				e.stopPropagation();
			// 				handleFlagged(item.addresses_id);
			// 			}}
			// 			title="Flag Rating">
			// 			<CustomIconButton type="flag" />
			// 		</button>
			// 		<button
			// 			className="btn-icon"
			// 			title="View Details"
			// 			onClick={(e) => {
			// 				e.stopPropagation();
			// 				setSelectedAddress(item);
			// 				setShowDetailModal(true);
			// 			}}>
			// 			<CustomIconButton type="view" />
			// 		</button>
			// 		<button
			// 			className="btn-icon btn-delete"
			// 			title="Delete Address"
			// 			onClick={(e) => {
			// 				e.stopPropagation();
			// 				handleDeleteAddress(item.addresses_id);
			// 			}}>
			// 			<CustomIconButton type="delete" />
			// 		</button>
			// 	</div>
			// ),
		},
	];
	return (
		<div className="admin-ratings-dashboard-page">
			<div className="page-header">
				<h2>Visitor Feedback</h2>
				<p>View and manage all visitor feedback messages ({totalLength} total)</p>
			</div>

			{/* Filters and Search */}
			<div className="ratings-controls">
				<div className="filter-section">
					{/* <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
						{statusOptions.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select> */}

					<select className="filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
						<option value="date-desc">Newest First</option>
						<option value="date-asc">Oldest First</option>
					</select>

					{/* <Autocomplete
						open={open}
						onOpen={() => setOpen(true)}
						onClose={() => setOpen(false)}
						options={options}
						getOptionLabel={(option) => option?.first_name + " " + option?.last_name || ""}
						loading={loadingUser}
						inputValue={inputValue}
						onInputChange={(e, value, reason) => {
							setInputValue(value);
							handleSearch(value);

							if (reason === "input") {
								const selectedUser = selectedUserId ? options.find((opt) => opt.user_id === selectedUserId) : null;
								const selectedUserName = selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : "";

								if (value !== selectedUserName) {
									setSelectedUserId(null);
								}
							}
						}}
						onChange={(e, value) => {
							if (value) {
								setSelectedUserId(value.user_id);
								setInputValue(value.first_name + " " + value.last_name);
							} else {
								setSelectedUserId(null);
								setInputValue("");
							}
						}}
						value={selectedUserId ? options.find((opt) => opt.user_id === selectedUserId) || null : null}
						clearIcon={<i className="fa-2xs fas fa-close" />}
						popupIcon={null}
						autoComplete
						includeInputInList
						filterSelectedOptions
						renderInput={(params) => (
							<TextField
								{...params}
								placeholder="User Name"
								sx={{
									"& .MuiOutlinedInput-root": {
										borderRadius: "10px",
										backgroundColor: "#fff",
										"& .MuiOutlinedInput-input": {
											padding: "10px 2px",
											height: 3,
										},
										"& fieldset": {
											borderColor: "#e9ecef",
											borderWidth: "2px",
										},
										"&:hover fieldset": {
											borderColor: "#e9ecef",
											borderWidth: "2px",
										},
										"&.Mui-focused fieldset": {
											borderColor: "#28a745",
											borderWidth: "2px",
											boxShadow: "0 0 0 .2rem #22c55e40",
										},
									},
								}}
								InputProps={{
									...params.InputProps,
									endAdornment: <>{params.InputProps.endAdornment}</>,
								}}
							/>
						)}
					/> */}

					{/* <input
						type="text"
						className="search-input"
						placeholder="Search by name, email, or message..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
					/> */}
				</div>
			</div>

			{loading ? (
				<div className="ratings-table-container">
					{contacts.length > 0 ? (
						<table className="ratings-table">
							<thead>
								<tr>
									<th>Name</th>
									<th>Email</th>
									<th>Phone</th>
									<th>Message</th>
									<th>User</th>
									<th>Status</th>
									<th>Date</th>
									{/* <th>Actions</th> */}
								</tr>
							</thead>
							<tbody>
								{contacts.map((contact) => (
									<tr key={contact.contact_us_id}>
										<td>{contact.fullName}</td>
										<td>{contact.email}</td>
										<td>{contact.phone || "N/A"}</td>
										<td>{truncateText(contact.message, 50)}</td>
										<td>{contact.user ? <CustomUserName user={contact.user} /> : <span className="text-muted">Guest</span>}</td>
										<td>{getStatusBadge(contact.status)}</td>
										<td>{formatDate(contact.submittedAt)}</td>
										{/* <td>
											<div className="action-buttons">
												<button className="btn-view" onClick={() => handleViewDetails(contact)}>
													View
												</button>
												<select
													className="status-select"
													value={contact.status}
													onChange={(e) => handleStatusUpdate(contact.contact_us_id, e.target.value)}
												>
													{statusOptions
														.filter((opt) => opt.value !== "all")
														.map((option) => (
															<option key={option.value} value={option.value}>
																{option.label}
															</option>
														))}
												</select>
												<button
													className="btn-delete"
													onClick={() => handleDeleteContact(contact.contact_us_id)}
													disabled={loadingButtons[`delete-${contact.contact_us_id}`]}
												>
													{loadingButtons[`delete-${contact.contact_us_id}`] ? (
														<CircularProgress size={16} />
													) : (
														"Delete"
													)}
												</button>
											</div>
										</td> */}
									</tr>
								))}
							</tbody>
						</table>
					) : (
						<div className="no-data">No contact us messages found</div>
					)}
				</div>
			) : (
				<CustomLoader />
			)}

			<CustomPagination
				current={page}
				pageSize={pageSize}
				// total={totalRecode}
				onChange={(e) => setPage(e)}
				scroll={{x: "max-content", y: 10 * 5}}
				onShowSizeChange={(p, e) => {
					setPage(p);
					setPageSize(e);
				}}
			/>

			{/* Pagination */}
			{/* {totalPages > 1 && (
				<div className="pagination">
					<button onClick={() => paginate(page - 1)} disabled={page === 1}>
						Previous
					</button>
					<span>
						Page {page} of {totalPages}
					</span>
					<button onClick={() => paginate(page + 1)} disabled={page === totalPages}>
						Next
					</button>
				</div>
			)} */}

			{/* Detail Modal */}
			{showModal && selectedContact && (
				<div className="modal-overlay" onClick={() => setShowModal(false)}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<div className="modal-header">
							<h3>Contact Us Details</h3>
							<button className="modal-close" onClick={() => setShowModal(false)}>
								×
							</button>
						</div>

						<div className="modal-body">
							<div className="detail-section">
								<h4>Contact Information</h4>
								<div className="detail-grid">
									<div className="detail-item">
										<span className="detail-label">Name:</span>
										<span className="detail-value">{selectedContact.fullName}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Email:</span>
										<span className="detail-value">{selectedContact.email}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Phone:</span>
										<span className="detail-value">{selectedContact.phone || "N/A"}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">User:</span>
										<span className="detail-value">{selectedContact.user ? <CustomUserName user={selectedContact.user} /> : "Guest"}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Status:</span>
										<span className="detail-value">{getStatusBadge(selectedContact.status)}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Submitted:</span>
										<span className="detail-value">{formatDate(selectedContact.submittedAt)}</span>
									</div>
								</div>
							</div>

							<div className="detail-section">
								<h4>Message</h4>
								<div className="message-content">{selectedContact.message}</div>
							</div>

							{selectedContact.admin_response && (
								<div className="detail-section">
									<h4>Admin Response</h4>
									<div className="message-content">{selectedContact.admin_response}</div>
								</div>
							)}

							{selectedContact.admin_notes && (
								<div className="detail-section">
									<h4>Admin Notes</h4>
									<div className="message-content">{selectedContact.admin_notes}</div>
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default ContactUs;
